import React, { useRef } from 'react';
import { useForm } from 'react-hook-form';
import Swal from 'sweetalert2';
import Grid from '../../components/UI/Grid';
import Text from '../../components/UI/Text';
import Button from '../../components/UI/Button';
import { useHistory } from 'react-router-dom';
import { gql, useMutation } from '@apollo/client';
import { IResetPasswordInput, IResetPasswordMutationOutput } from './dtos/reset-password-mutation.dto';
import { LoginInput } from '../../../dtos/globalTypes';

/**
 * @alias 비밀번호 변경 gql
 */
const RESET_PASSWORD_MUTATION = gql`
    mutation resetPasswordMutation($resetPasswordInput: ResetPasswordInput!) {
        resetPassword(input: $resetPasswordInput) {
            success
            message
        }
    }
`;

/**
 * @alias 비밀번호 변경 form interface
 */
interface IResetPassword extends Pick<LoginInput, 'password'> {
    /**
     * @alias 비밀번호 확인
     */
    confirmPassword: string;
}

function ResetPassword() {
    const {
        register,
        handleSubmit,
        formState: { errors },
        watch,
    } = useForm<IResetPassword>();
    const [userToken, setUserToken] = React.useState<string>('');
    const history = useHistory();

    //회원가입 전용, 비밀번호와 비밀번호 체크 일치여부 확인시 필요
    const password: any = useRef();
    //비밀번호에 입력한 값
    password.current = watch('password');

    const regexPassword = /^(?=.*[a-zA-Z])(?=.*[!@#$%^*+=\-,\\.<>/\\?~\\(\\)\\_\\[\]\\{\\}\\|;:])(?=.*[0-9]).{8,}$/; // 비밀번호 체크 정규식 (영문+숫자+특문조합 8자 이상)

    //#region 이벤트 처리

    /**
     * @alias queryParameter -> userToken 가져오기
     */
    React.useEffect(() => {
        const params = new URLSearchParams(window.location.search); // url param 가져오기
        const userToken = params.get('userToken');
        if (!userToken) {
            Swal.fire({
                title: '에러',
                html: `비정상적인 접근입니다.`,
                icon: 'error',
            });
            history.push({
                pathname: `/notfound`,
            });
        }
        setUserToken(userToken!);
    }, []);

    /**
     * @alias 쇼핑몰 로그인 정보 전송 함수
     */
    async function onSubmit(data: any) {
        const { confirmPassword } = data;

        try {
            // 무한 로딩 화면 생성
            Swal.fire({
                title: `비밀번호 변경중...`,
                html: '플레이오토 비밀번호를 변경하는 중입니다.', // add html attribute if you want or remove
                allowOutsideClick: false,
                showConfirmButton: false,
                willOpen: () => {
                    Swal.showLoading();
                },
            });

            // 비밀번호 변경 mutation 호출
            resetPasswordMutation({
                variables: {
                    resetPasswordInput: {
                        password: confirmPassword,
                        userToken: userToken!,
                    },
                },
            });
        } catch (e: any) {
            Swal.fire({
                title: '실패',
                html: `${e.message}`,
                icon: 'error',
            });
        }
    }

    /**
     * @alias 비밀번호 변경 완료 이벤트
     */
    async function onCompleted(data: IResetPasswordMutationOutput) {
        const {
            resetPassword: { success, message },
        } = data;
        if (success) {
            await Swal.fire({
                title: '성공',
                html: `${message}`,
                icon: 'success',
                confirmButtonText: '확인',
            });
            history.push('/member/login');
        } else {
            await Swal.fire({
                title: '실패',
                html: `${message}`,
                icon: 'error',
            });
        }
    }
    //#endregion 이벤트 처리

    //#region query, mutation 모음
    /**
     * @alias 비밀번호 변경 mutation
     */
    const [resetPasswordMutation] = useMutation<IResetPasswordMutationOutput, IResetPasswordInput>(
        RESET_PASSWORD_MUTATION,
        {
            onCompleted,
            //onError,
        },
    );
    //#endregion query, mutation 모음

    return (
        <div id="loginscss">
            <div id="containerH">
                <div className="loginArea">
                    <form className="loginForm" onSubmit={handleSubmit(onSubmit)}>
                        <Grid display="flex" alignItems="center" justifyContent="center" height={200}>
                            <Text fontSize="2rem" fontWeight={600}>
                                새로운 비밀번호로 변경
                            </Text>
                        </Grid>
                        <div>
                            {/* <img alt="" src={iconPass} /> */}
                            <input
                                name="password"
                                type="password"
                                placeholder="영문 + 숫자 + 특문조합 8자 이상"
                                ref={register({ required: true, minLength: 8, pattern: regexPassword })}
                            />
                            {errors?.password && errors.password.type === 'required' && (
                                <p>비밀번호는 필수 입력 항목입니다. </p>
                            )}
                            {errors?.password && errors.password.type === 'minLength' && (
                                <p>비밀번호는 8자 이상 입력해 주세요. </p>
                            )}
                            {errors?.password && errors.password.type === 'pattern' && (
                                <p>비밀번호는 영문+숫자+특문 8자 이상 입력해 주세요. </p>
                            )}
                        </div>

                        <div>
                            {/* <img alt="" src={iconPass} /> */}
                            <input
                                name="confirmPassword"
                                type="password"
                                placeholder="영문 + 숫자 + 특문조합 8자 이상"
                                ref={register({
                                    required: true,
                                    minLength: 8,
                                    pattern: regexPassword,
                                    validate: (value) => value === password.current,
                                })}
                            />
                            {errors?.confirmPassword && errors.confirmPassword.type === 'required' && (
                                <p>비밀번호체크는 필수 입력 항목입니다.</p>
                            )}
                            {errors?.confirmPassword && errors.confirmPassword.type === 'minLength' && (
                                <p>비밀번호는 8자 이상 입력해 주세요.</p>
                            )}
                            {errors?.confirmPassword && errors.confirmPassword.type === 'pattern' && (
                                <p>비밀번호는 영문+숫자+특문 8자 이상 입력해 주세요.</p>
                            )}
                            {errors?.confirmPassword && errors.confirmPassword.type === 'validate' && (
                                <p>비밀번호와 일치하지 않습니다.</p>
                            )}
                        </div>

                        <Button type="submit">비밀번호 변경</Button>
                    </form>
                </div>
            </div>
        </div>
    );
}

export default ResetPassword;
